import { useContext, useEffect, useRef, useState } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { QueryStatus, WorkspaceType } from "@shared/types";
import { SymbolsMetadataContext } from "@shared/features/common/SymbolsMetadata.context";
import { USAGE_TYPES } from "@shared/features/settings/constants";
import { selectWorkspace } from "@shared/slices/common.slice";
import { ClientsDataContext } from "@shared/features/common/ClientsData.context";
import { formatDate } from "@shared/utils/date";
import { SECONDS_IN_DAY } from "@shared/common/constants";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";

export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

export const useGetUser = (skip?: boolean) => {
  const token = useSelector(sharedLib.selectors.authentication.selectToken);
  const workspace = useSelector(selectWorkspace);
  const getRealUserQuery = sharedLib.sharedApi.authentication.useGetUserQuery(
    !skip && token
      ? {
          is_demo_query: false,
        }
      : skipToken
  );
  const getDemoUserQuery = sharedLib.sharedApi.authentication.useGetUserQuery(
    !skip && token
      ? {
          is_demo_query: true,
        }
      : skipToken
  );

  const refetch = () => {
    try {
      getRealUserQuery.refetch();
    } catch {}
    try {
      getDemoUserQuery.refetch();
    } catch {}
  };

  const queryData =
    workspace === WorkspaceType.DEMO ? getDemoUserQuery : getRealUserQuery;
  return { ...queryData, refetch };
};

export const useUser = (skip?: boolean) => {
  const { isLoading, isFetching, isError, refetch } = useGetUser(skip);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);

  return {
    user,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
};

interface useSharedIntercomProps {
  logout: () => void;
  update: () => void;
  login: () => void;
  isReady?: boolean;
}
export const useSharedIntercom = ({
  logout,
  login,
  update,
  isReady = true,
}: useSharedIntercomProps) => {
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loggedInUserId, setLoggedInUserId] = useState<number | undefined>();

  useEffect(() => {
    if (!isReady) return;
    let shouldBoot = !isInitialized;
    const userChanged =
      isInitialized && user && loggedInUserId && user.id !== loggedInUserId;
    const userLoggedOut = isInitialized && !user && loggedInUserId;
    const userUpdated =
      isInitialized && user && loggedInUserId && user.id === loggedInUserId;
    const userLoggedIn = isInitialized && user && !loggedInUserId;
    if (userChanged || userLoggedOut) {
      logout();
      shouldBoot = true;
    } else if (userUpdated) {
      update();
      shouldBoot = false;
    } else if (userLoggedIn) shouldBoot = true;
    if (shouldBoot) {
      login();
      setIsInitialized(true);
      setLoggedInUserId(user?.id);
    }
  }, [user?.id, isReady]);

  return isInitialized;
};

export const useQueryWithRetries = <Data = any, Args = any>(
  queryHook: (args?: Args, options?: any) => QueryStatus<Data, Args>,
  args?: Args,
  options?: any
) => {
  const [numRetries, setNumRetries] = useState(0);
  const [delay, setDelay] = useState(2000);
  const queryStatus = queryHook(args, options);
  const queryStatusRef = useRef<QueryStatus>();
  const isLoadingRef = useRef<boolean>();
  queryStatusRef.current = queryStatus;
  isLoadingRef.current = queryStatus.isFetching || queryStatus.isLoading;

  const resetState = () => {
    setNumRetries(0);
    setDelay(2000);
  };

  const retryQuery = () => {
    setTimeout(() => {
      try {
        if (queryStatusRef.current?.isError && !isLoadingRef.current) {
          queryStatusRef?.current?.refetch();
        }
      } catch {}
    }, delay);
  };

  useEffect(() => {
    if (isLoadingRef.current) return;
    if (queryStatusRef.current?.isError && numRetries < 7) {
      retryQuery();
      setNumRetries((numRetries) => numRetries + 1);
      setDelay((delay) => delay * 2);
    } else if (queryStatusRef.current?.isSuccess) {
      resetState();
    }
  }, [queryStatus]);

  return queryStatus;
};

export const useSymbolsMetadata = () => useContext(SymbolsMetadataContext)!;

export const useUserUsageTypes = () => {
  return USAGE_TYPES;
};

export const useUpdateValueWithInterval = (
  value: any,
  updateIntervalMs: number,
  randomWaitMs = 3000,
  valueKey?: string
) => {
  const [_, setRerender] = useState(0);
  const valueRef = useRef<any>(value);
  const debouncedValueRef = useRef<any>(value);
  const updatePendingRef = useRef(false);
  const firstRenderRef = useRef(true);
  const timeoutRef = useRef(null);
  const valueKeyRef = useRef(valueKey);
  const isMountedRef = useRef(true);

  useEffect(() => {}, [valueKey]);

  const scheduleRender = () => {
    const timeout = updateIntervalMs + Math.random() * randomWaitMs;
    setTimeout(() => {
      if (isMountedRef.current) {
        setRerender((rerender) => rerender + 1);
        updatePendingRef.current = false;
      }
    }, timeout);
  };

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    if (valueRef.current !== value && !updatePendingRef.current) {
      updatePendingRef.current = true;
      scheduleRender();
    }
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
};

export const useClientsData = () => useContext(ClientsDataContext)!;

export const useExchangesMetadata = () => useContext(ExchangesMetadataContext)!;

export const useUserInfo = () => {
  const { user } = useUser();
  const currentUnixTimestamp = Date.now() / 1000;
  const userExpirationTimestamp = user?.expiration_timestamp || 0;
  const expirationDate = formatDate(userExpirationTimestamp, false, true);
  const daysLeft =
    (userExpirationTimestamp - currentUnixTimestamp) / SECONDS_IN_DAY;

  const userInfoItems: { key: string; value: string; type: string }[] = [];
  if (user?.package) {
    userInfoItems.push({
      key: "Plan",
      value: user.getPackageDisplay(),
      type: "secondary",
    });
  }
  if (!user?.isFree() && !!user?.expiration_timestamp) {
    userInfoItems.push({
      key: `${user?.isExpired() ? "Expired" : "Expires"} on`,
      value: expirationDate,
      type: daysLeft < 1 ? "danger" : "secondary",
    });
  }

  return {
    userInfoItems,
  };
};

export const useLiveGroupSearch = (subGroupId?: number) => {
  const liveUpdateChannelsQuery =
    sharedLib.sharedApi.configuration.useLiveUpdateChannelsQuery(
      subGroupId ? { sub_group_id: subGroupId } : skipToken
    );
  const groupsData = liveUpdateChannelsQuery.data?.data.data.groups_data;
  const isNotMember =
    !!subGroupId && !liveUpdateChannelsQuery.isFetching && !groupsData?.length;

  return {
    data: liveUpdateChannelsQuery.data,
    isNotMember,
    isFetching: liveUpdateChannelsQuery.isFetching,
    liveUpdateChannelsQuery,
  };
};
